import { Navigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { LoadingState } from '@ridemaadi/ui';
import { ApiClientError } from '../../lib/api-client';
import { useAuth } from '../../providers/AuthProvider';
import { useAdminAccess } from '../hooks';

/** Gate /admin routes - server validates ADMIN role via /api/admin/access. */
export function AdminRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const access = useAdminAccess();

  if (authLoading || (isAuthenticated && access.isLoading)) {
    return <LoadingState label="Verifying administrator access…" />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  if (access.isError) {
    const err = access.error;
    if (err instanceof ApiClientError && err.status === 403) {
      return (
        <div className="flex min-h-dvh items-center justify-center bg-bg px-6 dark">
          <p className="text-center text-lg font-medium text-content">Administrator access required.</p>
        </div>
      );
    }
    if (err instanceof ApiClientError && err.status === 401) {
      return <Navigate to="/admin/login" replace />;
    }
    return (
      <div className="flex min-h-dvh items-center justify-center bg-bg px-6 dark">
        <p className="text-center text-muted">Unable to verify access. Try again later.</p>
      </div>
    );
  }

  return <>{children}</>;
}
